import i18n from '../../i18n';

export type ConnectedSourceId =
  | 'reservations'
  | 'tableplan'
  | 'pos'
  | 'alenopay'
  | 'ga'
  | 'googleads'
  | 'gsc'
  | 'gbp'
  | 'reviews'
  | 'crm'
  | 'weather'
  | 'events'
  | 'demandgen'
  | 'opentable';

// Canonical reservation booking-origin ("source"). Derived from Mongo
// reservation fields (createdFrom / creatorId / utm.source / walkIn) by the
// server-side classifier in opsCommand/reservationSource.ts. Used as both a
// filter and a comparison lens across Live Service and Performance.
export type ReservationSourceId =
  | 'widget'
  | 'reserveWithGoogle'
  | 'assistentAi'
  | 'staff'
  | 'walkIn'
  /** Created by a partner system's API user: a PMS, a POS, an import, a portal. */
  | 'integration'
  | 'other'
  /** POS baskets with no reservation.basketid link (source-mix only). */
  | 'unattributed';

// Canonical reservation-status groups, mapped server-side to numeric `state`
// ids (see semantics on both backends). Used as a multi-select lens across the
// Performance dashboard: an empty selection means "all statuses" (no filter).
export type ReservationStatusGroup =
  | 'confirmed'
  | 'waitlist'
  | 'seated'
  | 'done'
  | 'noshow'
  | 'cancelled'
  | 'rejected';

export type StatusFilter = ReservationStatusGroup[];

/** Configured shift display names (unioned across the current restaurant scope). */
export type ShiftFilter = string[];

export type ShiftCatalogItem = {
  name: string;
  outletCount: number;
  /** Every shift behind the name is flagged as an event. */
  isEvent?: boolean;
};

/** One event (a shift flagged as an event) with the period's reservations behind it. */
export type EventRow = {
  key: string;
  name: string;
  /** A date-bound event, or one that repeats. */
  kind: 'oneOff' | 'recurring';
  outletCount: number;
  archived: boolean;
  reservations: number;
  guests: number;
  /** Service days the event took served guests, per outlet and shift version. */
  occurrences: number;
  /** Guests over seats × occurrences; null without seats. */
  fillPct: number | null;
  noshowPct: number | null;
  cancelPct: number | null;
  sellRevenue: number;
  firstDate: string | null;
  lastDate: string | null;
  compare?: {
    reservations: number;
    guests: number;
    occurrences: number;
    fillPct: number | null;
    noshowPct: number | null;
    cancelPct: number | null;
    sellRevenue: number;
  };
};

/** opsCommand/getEvents: the scope's events for the Performance period. */
export type EventsOverview = {
  events: EventRow[];
  totals: { events: number; reservations: number; guests: number; oneOff: number; recurring: number };
  /** Events set up in the scope, and how many of them took no reservation in the period. */
  configured: number;
  quiet: number;
  periodLabel: string;
  comparable: boolean;
  comparePeriod?: { label: string };
};

// UI catalog for the status filter: ordered groups with human labels and a
// short tip. The state-id mapping lives on the backends (Python + TS) so the
// client never needs to know raw `state` numbers.
export const RESERVATION_STATUS_GROUPS: {
  id: ReservationStatusGroup;
  label: string;
  tip: string;
}[] = [
  {
    id: 'confirmed',
    get label() { return i18n.t('metrics:status.confirmed.label'); },
    get tip() { return i18n.t('metrics:status.confirmed.tip'); },
  },
  {
    id: 'waitlist',
    get label() { return i18n.t('metrics:status.waitlist.label'); },
    get tip() { return i18n.t('metrics:status.waitlist.tip'); },
  },
  {
    id: 'seated',
    get label() { return i18n.t('metrics:status.seated.label'); },
    get tip() { return i18n.t('metrics:status.seated.tip'); },
  },
  {
    id: 'done',
    get label() { return i18n.t('metrics:status.done.label'); },
    get tip() { return i18n.t('metrics:status.done.tip'); },
  },
  {
    id: 'noshow',
    get label() { return i18n.t('metrics:status.noshow.label'); },
    get tip() { return i18n.t('metrics:status.noshow.tip'); },
  },
  {
    id: 'cancelled',
    get label() { return i18n.t('metrics:status.cancelled.label'); },
    get tip() { return i18n.t('metrics:status.cancelled.tip'); },
  },
  {
    id: 'rejected',
    get label() { return i18n.t('metrics:status.rejected.label'); },
    get tip() { return i18n.t('metrics:status.rejected.tip'); },
  },
];

// One source's KPIs within a scope/period. `covers` is the volume driver and
// `share` its percentage of the scope total; quality metrics (noshow/cancel/
// seatTime) and sell-items revenue ride along so a single panel can compare
// both volume and quality across sources. `upcoming` is live-only (incoming
// covers, next 2h); `deltaPct` is the covers change vs the prior period for
// Performance and null for Live.
// Third-level utm.campaign within a widget sub-channel (e.g. Google Ads campaign).
export type SourceCampaignKpis = {
  key: string;
  label: string;
  covers: number;
  reservations: number;
  share: number;
  sellRevenue?: number;
};

// Second-level channel within a canonical source (e.g. utm.source inside Widget).
export type SourceSubChannelKpis = {
  key: string;
  label: string;
  covers: number;
  reservations: number;
  share: number;
  sellRevenue?: number;
  campaigns?: SourceCampaignKpis[];
  // Per-bucket covers aligned 1:1 with the parent source's `series` buckets
  // (Performance only). Keeps channel breakdown figures on the same bucket as
  // the parent source row headline.
  series?: number[];
};

export type SourceKpis = {
  source: ReservationSourceId;
  covers: number;
  reservations: number;
  share: number;
  noshow: number;
  cancel: number;
  seatTime: number;
  sellRevenue: number;
  upcoming: number;
  deltaPct: number | null;
  /** Share of reservations auto-seated by Brain for this source. */
  brainAutoPct: number;
  /** Additive Brain auto-seated booking count (mix-bar base). */
  autoSeated?: number;
  /** Additive no-show count (mix-bar base when drilling noshow). */
  noShowCount?: number;
  /** Additive cancel count (mix-bar base when drilling cancel). */
  cancelCount?: number;
  /** The channel's no-show rate base (its n). */
  noshowBase?: number;
  /** The channel's cancellation rate base (its n). */
  cancelBase?: number;
  subChannels?: SourceSubChannelKpis[];
  // Per-bucket covers across the selected Performance frame, aligned 1:1 with
  // the payload's `series` buckets. Default mini-trend for cover-volume drills.
  series?: number[];
  /** Per-bucket bookings (Performance only). */
  reservationSeries?: number[];
  /** Per-bucket no-show rate % (Performance only). */
  noshowSeries?: number[];
  /** Per-bucket cancellation rate % (Performance only). */
  cancelSeries?: number[];
  /** Per-bucket average seat time minutes (Performance only). */
  seatTimeSeries?: number[];
  /** Per-bucket sell-items revenue (Performance only). */
  sellSeries?: number[];
  /**
   * Measured POS revenue for this source via reservation.basketid → basket._id.
   * Unlinked baskets land on source `unattributed`.
   */
  posRevenue?: number;
  /** Linked reservation covers for the POS join (0 when unattributed). */
  posLinkedCovers?: number;
  /** Drill metric value derived from measured POS revenue. */
  posMetric?: number;
  /** Per-bucket POS metric series (aligned with `series` buckets). */
  posSeries?: number[];
  /** Per-bucket Brain auto-seated % (Performance only). */
  brainSeries?: number[];
  /** Compare-period covers for this source (present when comparable). */
  compareCovers?: number;
  /** Compare-period share of total covers (0–100). */
  compareShare?: number;
};

// Source mix for a scope: the per-source rows plus the total covers they sum
// to (so the client can render share bars and "vs all sources" context).
export type SourceBreakdown = {
  sources: SourceKpis[];
  totalCovers: number;
  /** ISO currency code when POS revenue was attached onto sources. */
  posCurrency?: string | null;
  /** Display unit for POS money (e.g. "CHF", "€"). */
  posUnit?: string;
  /** True when POS figures come from basket→reservation join. */
  posLinked?: boolean;
  /** True when attach bailed because restaurant-dominant currencies disagree. */
  posMixedCurrency?: boolean;
  /** @deprecated Cover-share proxy — always false when posLinked is set. */
  posAllocated?: boolean;
  posLinkedRevenue?: number;
  posUnattributedRevenue?: number;
};

// Raw per-source counters carried on each live Outlet. Kept as additive
// counters (not pre-divided rates) so that narrowing the scope to a subset of
// outlets can re-aggregate source KPIs faithfully in memory, the same way the
// region scope does for the headline KPIs.
export type SourceCampaignStat = {
  covers: number;
  reservations: number;
};

export type SourceSubChannelStat = {
  covers: number;
  reservations: number;
  campaigns?: Record<string, SourceCampaignStat>;
};

export type SourceOutletStat = {
  source: ReservationSourceId;
  covers: number;
  reservations: number;
  bookedCount: number;
  reservationCount?: number;
  noShowCount: number;
  /** Guest cancellations (STORNO), the cancellation-rate numerator. */
  cancelCount: number;
  guestCancelCount?: number;
  /** Rate bases (backend semantics.py): served + no-show; + guest cancellations. */
  noshowBase?: number;
  cancelBase?: number;
  seatMinSum: number;
  seatMinCount: number;
  sellRevenue: number;
  upcoming: number;
  subChannels?: Record<string, SourceSubChannelStat>;
};

// A single sold sell-item (aleno Pay pre-order) rolled up for an outlet: how
// many were pre-ordered and the revenue they represent.
export type SellItem = {
  name: string;
  emoji?: string;
  qty: number;
  revenue: number;
};

export type Outlet = {
  id: string;
  name: string;
  city: string;
  region: string;
  cuisine?: string;
  lat?: number;
  lng?: number;
  address?: string;
  seats: number;
  tables: number;
  seated: number;
  occTables: number;
  coversToday: number;
  upcoming: number;
  bookedAhead?: number;
  avgSeatMin: number;
  /** No-shows ÷ started served + no-show reservations (noshowBase). */
  noShow: number;
  /** Guest cancellations ÷ served + no-show + cancelled reservations (cancelBase). */
  cancelRate: number;
  cancels: number;
  noshowBase?: number;
  cancelBase?: number;
  committedCount?: number;
  bookedCount?: number;
  reservationCount?: number;
  noShowCount?: number;
  noShowCovers?: number;
  cancelCovers?: number;
  walkInCount?: number;
  walkInCovers?: number;
  waitlistCount?: number;
  waitlistCovers?: number;
  // Recovery ledger: today's cancellations / no-shows that freed a table, split
  // by whether the same table got rebooked within ±120 min (recovered), is
  // still ahead of now (recoverable), or passed unrecovered (lost). Gaps with no
  // table assignment land in gapUnknown and are excluded from the capture rate.
  gapCount?: number;
  gapCovers?: number;
  recoveredGapCount?: number;
  recoveredGapCovers?: number;
  recoverableGapCount?: number;
  recoverableGapCovers?: number;
  lostGapCount?: number;
  lostGapCovers?: number;
  gapUnknownCount?: number;
  gapUnknownCovers?: number;
  spark: number[];
  closed?: boolean;
  alert?: string;
  label?: string | null;
  flagged?: boolean;
  spendCover?: number;
  spendTrend?: number;
  review?: number;
  reviewDelta?: number;
  reviewMomentum?: number;
  reviewVol?: number;
  reviewSeries?: number[];
  webSessions?: number;
  widgetConv?: number;
  // aleno Pay sell-items (pre-ordered / pre-paid via the reservation widget):
  // the only first-party financial signal available without a POS connection.
  sellRevenue?: number;
  sellCount?: number;
  sellAttach?: number;
  sellItems?: SellItem[];
  // Per-source raw counters for today's service (see SourceOutletStat). Present
  // when the live snapshot was built with source attribution.
  sourceStats?: SourceOutletStat[];
  weather?: {
    cond: string;
    icon: string;
    temp: number;
    impact: string;
    terrace: boolean;
    mult: number;
    // Outlook for the evening / dinner-service window later today. Present when
    // there are still evening hours ahead; `changing` flags a meaningful shift
    // from the current conditions.
    later?: {
      cond: string;
      icon: string;
      temp: number;
      impact: string;
      terrace: boolean;
      mult: number;
      hour: number;
      changing: boolean;
    };
  };
  events?: {
    name: string;
    type: string;
    start: number;
    dist: number;
    surge: boolean;
    impact: string | number;
    address?: string;
    lat?: number;
    lng?: number;
  }[];
};

export type GroupKpis = {
  occupancy: number;
  seats: number;
  seated: number;
  tables: number;
  occTables: number;
  covers: number;
  incoming: number;
  seatTime: number;
  noshow: number;
  efficiency: number;
  turns: number;
  cancel: number;
  reviewMom: number;
  revenue: number;
  spend: number;
  review: number;
  web: number;
  // aleno Pay sell-items rolled up across the (open) fleet.
  sellRevenue: number;
  sellCount: number;
  sellAttach: number;
  outlets: number;
};

export type RegionSummary = {
  region: string;
  count: number;
  seats: number;
  seated: number;
  occ: number;
  alerts: number;
};

export type ScopeGroupDataSource = 'opentable';

export type ScopeGroupSummary = {
  id: string;
  name: string;
  count: number;
  occ: number;
  alerts: number;
  dataSource?: ScopeGroupDataSource;
};

export type FleetRestaurant = {
  id: string;
  name: string;
  city: string;
  region: string;
  alenoPay?: boolean;
  brainMode?: string | null;
  daysInAdvance?: number | null;
};

export type ScopeGroupConfig = {
  id: string;
  name: string;
  /** The venues of the group the person can open (the server drops the others). */
  restaurantIds: string[];
  dataSource?: ScopeGroupDataSource;
  /** Venues saved in the group the person can no longer open. */
  deniedCount?: number;
  /** How a group built from filters was made (lib/scopeGroupRecipe.ts); opaque to the server. */
  recipe?: unknown;
  createdAt?: number;
  updatedAt?: number;
};

export type OpsScopeConfigPayload = {
  restaurants: FleetRestaurant[];
  /** The person's own venue groups for this customer, kept on the server (decision 7.7.2). */
  groups: ScopeGroupConfig[];
  /** The customer (org) the restaurant belongs to: the view context is kept per person and org. */
  orgId?: string | null;
  /** "All venues of <group>": the opened venue's group, the venues of it the person can open. */
  orgVenues?: { name: string | null; restaurantIds: string[] };
};

export type ForecastPoint = {
  h: number;
  occ: number;
  covers: number;
  /** Concurrent guests in the hour (timeline Y-axis). */
  guests?: number;
  predicted: boolean;
};

/** Non-PII timeslot / reservation bars for the Live Service Timeline. */
export type ServiceTimelineSlot = {
  timeMs: number;
  guests: number;
  capacity: number | null;
  locked?: boolean;
};

/** Sanitized Aleno customer tag (no guest identity). */
export type GuestTag = {
  name: string;
  emoji?: string | null;
  /** Validated CSS hex from Aleno ``CustomerTagColor.hex``. */
  color?: string | null;
};

export type ServiceTimelineReservation = {
  id: string;
  outletId: string;
  outletName: string;
  peopleCount: number;
  startMs: number;
  endMs: number;
  state: number;
  channel: string;
  createdAtMs: number | null;
  seatedAtMs?: number | null;
  departedAtMs?: number | null;
  changedAtMs?: number | null;
  durationMs?: number | null;
  tableNames?: string[];
  category?: string | null;
  campaign?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  shiftId?: string | null;
  shiftName?: string | null;
  roomId?: string | null;
  /** Guest VIP flag — no identity fields. */
  isVip?: boolean;
  /** Aleno customer tags for this booking (name / emoji / color only). */
  guestTags?: GuestTag[];
  /** Guest-facing reservation comment (HTML stripped server-side). */
  guestNote?: string | null;
  /** Staff reservation comment. */
  staffNote?: string | null;
};

/** Per-outlet kitchen / floor briefing aggregates (no guest identity). */
export type KitchenBriefingRequest = {
  text: string;
  kind: 'comment' | 'reservationComment' | 'general';
  startMs: number;
  peopleCount: number;
  tables?: string | null;
  /** Links to ServiceTimelineReservation.id for the focus drawer. */
  reservationId?: string | null;
};

export type KitchenBriefingOutlet = {
  outletId: string;
  vipParties: Array<{
    startMs: number;
    peopleCount: number;
    tables?: string | null;
    reservationId?: string | null;
  }>;
  tagCounts: Array<GuestTag & { count: number }>;
  foodNotes: Array<{ text: string; count: number }>;
  seatingNotes: Array<{ text: string; count: number }>;
  /** One entry per reservation note — bound to time / party / tables. */
  requests: KitchenBriefingRequest[];
};

export type KitchenBriefing = {
  outlets: KitchenBriefingOutlet[];
  /**
   * False when the separate customer-tag GraphQL enrichment failed or the
   * day query fell back without customer ids. Empty tags with ``true`` means
   * none of tonight's bookings carry tags.
   */
  tagsAvailable?: boolean;
};

/** Shift window for Load curve / Bookings overlays (non-PII). */
export type ServiceTimelineShift = {
  id: string;
  outletId: string;
  name: string;
  startMs: number;
  endMs: number;
};

export type ServiceTimelineOps = {
  slots: ServiceTimelineSlot[];
  reservations: ServiceTimelineReservation[];
  shifts?: ServiceTimelineShift[];
};

export type ForecastData = {
  pts: ForecastPoint[];
  peak: ForecastPoint;
  peakMin: number;
  currentCovers: number;
  projectedCovers: number;
  projRevenue: number;
  noShowCovers: number;
  cancelCovers: number;
  atRisk: number;
  nowH: number;
  // Real service-day window (hours, extended past 24 for post-midnight services).
  openH: number;
  closeH: number;
};

export type InsightSeverity = 'alert' | 'opportunity' | 'info';

export type Insight = {
  id: string;
  sev: InsightSeverity;
  attach: string;
  title: string;
  why: string;
  action: string;
  src?: ConnectedSourceId | null;
};

// ---- System-wide settings deep dive ----
// The settings audit reads each shift's configuration straight from Mongo,
// turns it into a comparable profile, clusters venues with a similar posture
// into named archetypes, and rolls every misconfiguration up into one
// deduplicated, actionable feedback item per issue type (not one card per
// shift). Three views ride on this payload: archetype clusters, a colour-coded
// settings matrix (restaurant rows, per-shift drill-in), and the issue rollup.

// Verdict for a single setting value, relative to hard rules and fleet norms.
export type SettingStatus = 'ok' | 'warn' | 'alert';

// The five lenses the matrix groups settings under.
export type SettingsGroupKey =
  | 'capacity'
  | 'turnover'
  | 'window'
  | 'party'
  | 'guest'
  | 'setup';

// One matrix column's metadata, shipped so the client renders groups/labels
// (and a header tooltip) without re-encoding the catalog.
export type SettingsColumn = {
  key: string;
  label: string;
  group: SettingsGroupKey;
  groupLabel: string;
  tip: string;
};

export type LockEventKind =
  | 'onlineDay'
  | 'staffDay'
  | 'onlineSlot'
  | 'staffSlot';

/** One manual availability lock surfaced in the Settings locks tooltip. */
export type LockEvent = {
  ms: number;
  date: string;
  time?: string;
  kind: LockEventKind;
  shiftName?: string;
};

export type LockTimeline = {
  past: LockEvent[];
  future: LockEvent[];
};

// One setting's value + verdict for a shift (or the representative restaurant
// row). `display` is the human-readable value; `num` carries the numeric for
// benchmarking / sorting where the setting is numeric; `tip` explains a
// non-ok verdict on hover.
export type SettingCell = {
  key: string;
  display: string;
  status: SettingStatus;
  num?: number | null;
  tip?: string;
  lockTimeline?: LockTimeline;
};

// Whether a shift's configuration is in force now, starts later, or has ended.
export type ShiftState = 'active' | 'upcoming' | 'ended';

// A single shift's extracted settings profile: the CSV columns plus the
// derived signals (resolved dwell, single-seating, effective booking horizon,
// broken group flow) that make the feedback actionable.
export type ShiftSettingsProfile = {
  shiftId: string;
  name: string;
  state: ShiftState;
  serviceWindow: string;
  seats: number;
  onlineCeiling: number;
  maxPerTimeslot: number | null;
  dwellMin: number | null; // null = held until shift end (single seating)
  singleSeating: boolean;
  seatingsPerService: number;
  daysInAdvance: number;
  dailyDeadlineDays: number;
  peopleCountMin: number | null;
  peopleCountMax: number | null;
  groupsEnabled: boolean;
  groupFlowBroken: boolean;
  guestCancel: boolean;
  reminderEmail: boolean;
  reconfirmationEmail: boolean;
  questionsEnabled: boolean;
  cells: SettingCell[];
  worstStatus: SettingStatus;
};

// A restaurant's representative profile (one matrix row), aggregated across its
// shifts, plus the per-shift breakdown for the drill-in.
export type RestaurantSettingsProfile = {
  outletId: string;
  outletName: string;
  archetypeId: string;
  shiftCount: number;
  cells: SettingCell[];
  worstStatus: SettingStatus;
  shifts: ShiftSettingsProfile[];
};

// A named cluster of restaurants sharing a settings posture.
export type SettingsArchetype = {
  id: string;
  label: string;
  description: string;
  defining: string[];
  tone: SettingStatus;
  outletIds: string[];
};

// Fleet-wide reference for a numeric setting, for matrix context.
export type SettingsBenchmark = {
  key: string;
  label: string;
  median: number | null;
  unit?: string;
};

// One issue affecting a venue/shift, listed under a deduplicated issue group.
export type SettingsIssueInstance = {
  outletId: string;
  outletName: string;
  shiftName?: string;
  detail?: string;
};

// A single deduplicated issue type spanning the fleet: stated once, with the
// real mechanism (impact), the fix (recommendation), and every affected place.
export type SettingsIssueGroup = {
  id: string;
  category: SettingsGroupKey | 'status';
  severity: InsightSeverity;
  title: string;
  impact: string;
  recommendation: string;
  affected: SettingsIssueInstance[];
};

export type SettingsAuditPayload = {
  columns: SettingsColumn[];
  archetypes: SettingsArchetype[];
  restaurants: RestaurantSettingsProfile[];
  benchmarks: SettingsBenchmark[];
  issueGroups: SettingsIssueGroup[];
  outletsChecked: number;
  shiftsChecked: number;
};

// Billed products per restaurant, each checked against its own data
// (opsCommand/getSubscriptionUsage). Copy is the client's.
// One per product in HubSpot's product library (one-off fees aside).
export type SubscriptionFeatureKey =
  | 'pos'
  | 'channelManager'
  | 'pms'
  | 'pmsAutomation'
  | 'alenoPayNoShow'
  | 'alenoPayTransactions'
  // aleno Insights: the Looker dashboards that came before aleno intelligence.
  | 'insights'
  | 'sms'
  | 'respondelligent'
  // Automatic table assignment, billed as "Upgrade Digital Heart"; not checked yet.
  | 'upgrade'
  | 'intelligence'
  | 'licence'
  | 'other';

export type SubscriptionStatus =
  | 'inUse'
  | 'partial'
  | 'unused'
  | 'included'
  | 'unchecked';

export type SubscriptionFeature = {
  key: SubscriptionFeatureKey;
  // Line item names as billed, e.g. "POS Integration".
  items: string[];
  status: SubscriptionStatus;
  // POS: the last basket received (epoch ms), null when none in a year.
  lastUsedAt?: number | null;
  // Channel Manager / PMS / PMS Automation: reservations through them in the
  // window (PMS Automation: the ones a PMS rule created).
  reservations?: number;
  rwgReservations?: number;
  // PMS: hotel stays the PMS sent for the window or the next 60 days, and
  // whether the PMS is switched on in settings.
  hotelStays?: number;
  switchedOn?: boolean;
};

export type SubscriptionRestaurant = {
  restaurantId: string;
  restaurantName: string;
  features: SubscriptionFeature[];
  unusedCount: number;
  partialCount: number;
};

export type SubscriptionUsagePayload = {
  windowDays: number;
  restaurants: SubscriptionRestaurant[];
  // Venues with no billed items of their own (e.g. billed on a group contract).
  withoutSubscription: { restaurantId: string; restaurantName: string }[];
  unusedCount: number;
  partialCount: number;
};

export type DemandIntelOutlet = {
  restaurantId: string;
  restaurantName: string;
  hasAudit: boolean;
  placeId?: string;
  auditExportedAt?: string;
};

export type DemandBridgeOutletRow = {
  restaurant: string;
  searchesMonthly: number;
  mapsVisibilityPct: number;
  rwgBooked: number;
  rwgCovers: number;
  rwgRevenue: number;
  // Self-serve (widget + Reserve with Google) vs staff-keyed covers for this
  // outlet over the period, and the resulting online booking ratio. `null` when
  // the outlet had no online/staff covers to split.
  onlineCovers: number;
  staffCovers: number;
  onlineRatioPct: number | null;
};

// One outlet plotted on the "audit visibility → online booking share" scatter.
export type DemandVisibilityOnlinePoint = {
  restaurant: string;
  mapsVisibilityPct: number;
  onlineRatioPct: number;
  onlineCovers: number;
  staffCovers: number;
};

// Cross-sectional showcase: do outlets with stronger Google audits capture a
// larger share of bookings online (vs staff-keyed)? Computed across the scoped
// outlets that have an audit; null when fewer than 3 qualify.
export type DemandVisibilityOnline = {
  correlationR: number;
  outletCount: number;
  topTierOnlineRatioPct: number;
  bottomTierOnlineRatioPct: number;
  tierDeltaPp: number;
  points: DemandVisibilityOnlinePoint[];
};

export type DemandBridgeSection = {
  totalSearchesMonthly: number;
  mapsVisibilityPct: number;
  reserveWithGoogleBooked: number;
  reserveWithGoogleCovers: number;
  reserveWithGoogleRevenue: number;
  reserveWithGoogleSharePct: number;
  avgTicketEur: number;
  avgPartySize: number;
  missedRevenueEurLow: number;
  missedRevenueEurMid: number;
  missedRevenueEurHigh: number;
  reGroundedMissedRevenueEur: number;
  brokenLinkAlert: boolean;
  brokenLinkReason: string | null;
  gbpReservationUrl: string | null;
  perOutlet: DemandBridgeOutletRow[];
  visibilityOnline: DemandVisibilityOnline | null;
};

export type DemandHourlyRow = {
  day: string;
  hour: number;
  googlePopularIndex: number;
  alenoCovers: number;
};

export type DemandOpportunityRow = {
  query: string;
  searchVolume: number;
  isFound: boolean;
  cluster: string;
  hasMatchingShift: boolean;
  note: string;
};

export type DemandCapacitySection = {
  hourly: DemandHourlyRow[];
  shiftUsage: Array<{ shift: string; covers: number; operatingDays: number }>;
  opportunities: DemandOpportunityRow[];
  peakGoogleHour: number | null;
  peakAlenoHour: number | null;
  troughHours: number[];
};

export type DemandReviewThemeRow = {
  label: string;
  frequency: number;
  severity: string | null;
  operationalMetric: string | null;
  operationalValue: number | string | null;
};

export type DemandWeeklyOverlayRow = {
  week: string;
  sentimentScore: number;
  avgRating: number;
  reviewCount: number;
  covers: number;
  noShowRatePct: number;
};

export type DemandReviewVelocity = {
  totalReviews: number;
  rating: number;
  velocity30d: number;
  competitorVelocity30d: number;
  responseRatePct: number;
  competitorResponseRatePct: number;
  reviewsPer100Covers: number;
  competitorName: string;
  competitorRating: number;
  momentumLabel: string;
};

export type DemandReviewOpsSection = {
  velocity: DemandReviewVelocity;
  weeklyOverlay: DemandWeeklyOverlayRow[];
  risks: DemandReviewThemeRow[];
  strengths: DemandReviewThemeRow[];
};

export type DemandIntelPayload = {
  enabled: boolean;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  outlets: DemandIntelOutlet[];
  bridge: DemandBridgeSection | null;
  capacity: DemandCapacitySection | null;
  reviewOps: DemandReviewOpsSection | null;
};

export type GuestReviewTimelineRow = {
  bucketKey: string;
  label: string;
  avgRating: number;
  reviewCount: number;
  negativeCount: number;
  positiveCount: number;
  responseRatePct: number;
  covers: number;
};

export type GuestReviewOutletBucket = {
  bucketKey: string;
  label: string;
  reviewCount: number;
  avgRating: number;
};

export type GuestReviewOutletRow = {
  restaurantId: string;
  name: string;
  hasTarget: boolean;
  platforms: ('google' | 'tripadvisor')[];
  avgRating: number;
  reviewCount: number;
  ratingDelta: number | null;
  negativeSharePct: number;
  responseRatePct: number;
  unansweredNegative: number;
  vsFleetAvg: number;
  series?: GuestReviewOutletBucket[];
};

export type GuestReviewInsightCard = {
  tone: 'win' | 'risk' | 'watch';
  icon:
    | 'trendUp'
    | 'flag'
    | 'activity'
    | 'users'
    | 'star'
    | 'check';
  title: string;
  value: string;
  detail: string;
  action: string;
  priority: number;
};

export type GuestReviewsFleetSummary = {
  avgRating: number;
  totalReviews: number;
  responseRatePct: number;
  unansweredNegativeCount: number;
  reviewsPer100Covers: number;
  ratingDistribution: Array<{ stars: number; count: number }>;
  byPlatform: Array<{
    platform: 'google' | 'tripadvisor';
    count: number;
    avgRating: number;
  }>;
};

export type GuestReviewsOutletMeta = {
  restaurantId: string;
  restaurantName: string;
  hasTarget: boolean;
  platforms: ('google' | 'tripadvisor')[];
};

export type GuestReviewsPayload = {
  enabled: boolean;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  aggregation: PerfAggregation;
  comparable: boolean;
  dateRange: { start: string; end: string } | null;
  outlets: GuestReviewsOutletMeta[];
  fleet: GuestReviewsFleetSummary | null;
  timeline: GuestReviewTimelineRow[];
  outletRows?: GuestReviewOutletRow[];
  insights: GuestReviewInsightCard[];
};

export type RegimeCell = {
  day: number;
  shift: string;
  value: number;
  vsTypical: number;
};

export type RegimeOccupancy = {
  id: string;
  label: string;
  seats: number;
  peakSeated: number;
  occupancy: number;
  active: boolean;
};

export type ShiftCapacityInfo = {
  regimes: RegimeOccupancy[];
  activeRegime: string | null;
  activeRegimeLabel: string | null;
  activeSeats: number;
};

export type KpiComparisons = {
  coversDeltaPct: number | null;
  coversRefLabel: string;
  seatTimeDeltaMin: number | null;
  noshowDeltaPt: number | null;
  cancelDeltaPt: number | null;
};

// Same-weekday × time-of-day averages over the prior four weeks, replayed at
// the datetime picker's clock time. Powers benchmark hints on Service timeline.
export type ServiceTimelineBenchmarks = {
  regimeLabel: string | null;
  sampleWeeks: number;
  covers: number | null;
  turns: number | null;
  noShowsSoFar: number | null;
  seatTime: number | null;
  revenue: number | null;
  occupancy: number | null;
  seated: number | null;
  seats: number | null;
  occTables: number | null;
  tables: number | null;
  outlets: number | null;
  incoming: number | null;
  projectedCovers: number | null;
  peakPct: number | null;
  peakMin: number | null;
  noShowCovers: number | null;
  cancelCovers: number | null;
  projRevenue: number | null;
  noshow: number | null;
  cancel: number | null;
  efficiency: number | null;
  spend: number | null;
  sellRevenue: number | null;
  sellCount: number | null;
  sellAttach: number | null;
};

// The global Performance dashboard settings: a bucket granularity
// ("aggregation") and a lookback window ("frame") of `frameCount` × `frameUnit`.
// Replaces the old single PerformancePeriod enum. Validation + defaults live in
// lib/perfSettings.ts (mirrored on both backends).
export type PerfAggregation = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type PerfFrameUnit = 'day' | 'week' | 'month' | 'year';

// Which capacity definition occupancy% and the rhythm drill-down use.
// Re-bases per-slot capacity under the active basis.
//  - `seats`    → shift `internal.availableSeats` (staff-configured seating)
//  - `online`   → `capacityTechnically + buffer` (bookable online ceiling)
//  - `physical` → table-plan / room seats (the physical room)
export type CapacityBasis = 'seats' | 'online' | 'physical';

/**
 * Reference period (B) when comparing against the primary reporting window:
 * none, the previous period, last year by weekday (364 days back, the
 * default), last year by date, or custom dates (lib/perfSettings.ts).
 */
export type PerfCompareKind = 'off' | 'prior' | 'yoy_weekday' | 'yoy' | 'custom';

export interface PerfSettings {
  aggregation: PerfAggregation;
  frameCount: number;
  frameUnit: PerfFrameUnit;
  /** Inclusive calendar start (`YYYY-MM-DD`). With `dateEnd`, selects explicit range mode. */
  dateStart?: string | null;
  /** Inclusive calendar end (`YYYY-MM-DD`). With `dateStart`, selects explicit range mode. */
  dateEnd?: string | null;
  /** Capacity denominator for Occupancy / Turns / RevPASH. Defaults to `seats`. */
  capacityBasis?: CapacityBasis;
  /**
   * Reference period preset; counts only when `compareExplicit`. Default on
   * every screen: `yoy_weekday` (decision 7.5.3).
   */
  compareKind?: PerfCompareKind;
  /**
   * True when the person chose the comparison (on any screen, or `cm` in the
   * URL); every screen then uses it, otherwise the default. Only explicit
   * choices go into the URL.
   */
  compareExplicit?: boolean;
  /** Inclusive compare start when `compareKind === 'custom'`. */
  compareDateStart?: string | null;
  /** Inclusive compare end when `compareKind === 'custom'`. */
  compareDateEnd?: string | null;
}

// One aggregation bucket of the frame, used for the over-time trend chart and
// the outlet sparklines. Rates are already resolved to percentages.
export interface BucketPoint {
  key: string;
  label: string;
  covers: number;
  noshow: number;
  cancel: number;
  sell: number;
}

export type PerformanceMetric = {
  key: string;
  label: string;
  value: number;
  prior: number;
  unit?: string;
  locked?: boolean;
  /** Shown on the card face when denominators were restricted (e.g. POS coverage). */
  note?: string;
  /** Preferred over `note` — short stacked lines (math, coverage). */
  noteLines?: string[];
  // Per-bucket values across the selected frame, aligned 1:1 with the payload's
  // `series` buckets (same order/labels). Lets each tile show the metric broken
  // down per aggregation period instead of only the whole-frame total. Omitted
  // for locked metrics with no backing data.
  series?: number[];
  /** Per-bucket values for the reference period when bucket counts align with `series`. */
  compareSeries?: number[];
};

export type OutletPerformanceRow = {
  outlet: Outlet;
  periodCovers: number;
  periodOccupancy: number;
  periodNoshow: number;
  periodCancel: number;
  // Guests delta vs the immediately preceding period (not calendar YoY).
  vsPriorPct: number;
  // This outlet's covers per aggregation bucket, aligned 1:1 with the payload's
  // `series` buckets. Powers the per-outlet trend sparkline and the
  // latest-vs-previous-bucket delta.
  spark: number[];
  // Full period scorecard metrics for this outlet (same keys as fleet `metrics`).
  metrics: PerformanceMetric[];
};

export type DrillDownPerspective = {
  label: string;
  value: string;
  hint?: string;
  scope?: 'now' | 'today' | 'planned';
  /** Compare-period value (present when `comparable`). */
  compareValue?: string;
  /** Signed delta vs compare, e.g. "+12%" or "+1.2pt". */
  compareDelta?: string | null;
};

// Provenance for a drill-down panel. Wider than MetricProvenance because some
// panels surface estimated figures (e.g. revenue exposure without POS).
export type DrillProvenance = 'measured' | 'inferred' | 'projected' | 'estimated';

// One bucket of a driver breakdown: the metric value within the bucket, how
// many reservations fed it, and how far it sits from the metric's baseline.
export type DriverBucket = {
  label: string;
  /** Rate (%), average, or — for share dims — the raw amount. */
  value: number;
  sample: number;
  /** % vs the pooled baseline for rate/avg; null for share dims. */
  vsBaselinePct: number | null;
  /** Share dims: 0–100 of the classified (non-Unknown) total; null for Unknown. */
  sharePct?: number | null;
  /** Enough sample (and, for rates, z ≥ 1.64 vs the rest) to read as a driver. */
  significant?: boolean;
  /** Fewer reservations than the driver minimum; drawn muted, never a top driver. */
  thin?: boolean;
  /** Secondary metric value for combined drill-down screens. */
  value2?: number;
  sample2?: number;
  /** Compare-period value for this bucket (present when `comparable`). */
  compareValue?: number;
  /** Compare-period sample size for this bucket. */
  compareSample?: number;
  /** Signed delta vs the compare period (pct for rate/avg, pt for share). */
  vsComparePct?: number | null;
};

// A single analyzed dimension (lead time, party size, channel, ...). `mode`
// tells the client how to read the values: a rate (%), an average, or a share
// of volume. `baseline` is the reference the buckets are compared against.
export type DriverDimension = {
  id: string;
  label: string;
  mode: 'rate' | 'avg' | 'share';
  baseline: number;
  buckets: DriverBucket[];
  topDriver: DriverBucket | null;
  bottomDriver: DriverBucket | null;
  provenance: DrillProvenance;
  /** How to format bucket values when they differ from the hero metric (e.g. rate drivers show cover counts). */
  unit?: 'eur' | 'num' | 'pct' | 'min' | 'x' | string;
  /** When true, above-baseline rate/avg is good (Brain, POS money). */
  higherIsBetter?: boolean;
  /** Noun for share-mode subtitles (covers / revenue / occupancy / turns). */
  shareOf?: string;
  /** Secondary metric baseline for combined drill-down screens. */
  baseline2?: number;
  /** Fleet baseline for the compare period (present when `comparable`). */
  compareBaseline?: number;
  /** Guests/reservations with a known bucket vs all in the window. */
  classified?: number;
  total?: number;
  /** Volume in the bars actually drawn (thick + Unknown); may be below `total`. */
  shown?: number;
};

/** One weekday × shift cell (or weekday row / column / total) of the drill grid. */
export type DrillGridEntry = {
  /** The metric over this cell; null when nothing stands behind it. */
  value: number | null;
  /** Reservations behind the value (the metric's own base for rates). */
  n: number;
  /** Fewer reservations than `minSample`: draw muted, never call out. */
  muted: boolean;
  /** Rate metrics: differs from the rest beyond normal variation. */
  significant?: boolean;
  compareValue?: number | null;
  compareN?: number;
  /** Change vs compare: points for rates (`pt`), percent otherwise. */
  delta?: number | null;
  deltaUnit?: 'pt' | '%';
};

/** Weekday × shift grid for the Performance drill (drill_grid.py). */
export type DrillGrid = {
  metricKey: string;
  /** `dayparts` when an outlet that served guests has no shift config. */
  mode: 'shifts' | 'dayparts';
  /** ISO weekdays, Monday (1) first, labelled in the venue's language. */
  rows: { dow: number; label: string }[];
  columns: { key: string; label: string; isEvent?: boolean }[];
  cells: (DrillGridEntry & { dow: number; col: string })[];
  rowTotals: (DrillGridEntry & { dow: number })[];
  colTotals: (DrillGridEntry & { col: string })[];
  /** Pooled over every cell: the hero for counts and rates. */
  total: DrillGridEntry;
  nUnit: 'reservations';
  minSample: number;
  comparable: boolean;
  operatingDays: number;
  /** The one cell the tile may call out (enough reservations, and for a rate significant). */
  peak?: { dow: number; col: string };
};

/** Guests (or reservations) by weekday × local start hour (drill_grid.py). */
export type DrillHourProfile = {
  metricKey: string;
  measure: 'covers' | 'booked' | 'confirmed' | 'noshowGuests' | 'cancelGuests';
  unit: 'guests' | 'reservations';
  cells: { dow: number; hour: number; value: number }[];
  total: number;
};

/** Guests drill hero extras: per operating day and the walk-in split. */
export type DrillGuestsHero = {
  operatingDays: number;
  guestsPerOperatingDay: number | null;
  walkInGuests: number;
  reservationGuests: number;
  compareOperatingDays?: number;
  compareGuestsPerOperatingDay?: number | null;
  compareWalkInGuests?: number;
  compareReservationGuests?: number;
};

/** The reservations and guests behind a figure (every tile prints its n). */
export type DrillBasis = { reservations: number; guests: number };

// Ordered, discriminated list of metric-specific panels rendered beneath the
// generic drill-down shell. The client switches on `kind`; unknown kinds are
// ignored so the server can add panels without breaking older clients.
export type DrillPanel =
  | { kind: 'summaryCards'; title?: string; cards: DrillDownPerspective[] }
  | {
      kind: 'ranking';
      title: string;
      /** Money unit may be a currency code/symbol (CHF, €) for POS panels. */
      unit?: 'eur' | 'num' | 'pct' | 'min' | 'x' | string;
      provenance?: DrillProvenance;
      rows: {
        id: string;
        name: string;
        value: number;
        sub?: string;
        /** Compare-period value (present when `comparable`). */
        compareValue?: number;
      }[];
      /** What stands behind the ranking (pre-ordered items, reservations). */
      sample?: { n: number; unit: 'items' | 'reservations' };
    }
  | {
      /** Navigable POS article ranking (name / qty / revenue / avg price). */
      kind: 'posItems';
      title: string;
      unit?: string;
      provenance?: DrillProvenance;
      totalRevenue: number;
      items: {
        id: string;
        name: string;
        revenue: number;
        qty: number;
        currency?: string;
        avgPrice: number;
        /** Compare-period revenue (present when `comparable`). */
        compareRevenue?: number;
      }[];
    }
  | {
      kind: 'drivers';
      title: string;
      dimensions: DriverDimension[];
      /** Which metric the buckets represent (e.g. seatTime on the Turns drill). */
      metricKey?: string;
      /** Set when a drivers panel uses a window other than the selected frame. */
      windowNote?: string;
    }
  | {
      kind: 'bySource';
      title: string;
      // How to read each row's `value` (matches the active metric).
      // POS drills pass a currency code/symbol (CHF, €) instead of 'eur'.
      unit?: 'eur' | 'num' | 'pct' | 'min' | 'x' | string;
      provenance?: DrillProvenance;
      rows: {
        source: ReservationSourceId;
        // Metric value for this source (e.g. covers, no-show %, sell €).
        value: number;
        /** Secondary metric value for combined drill-down screens. */
        value2?: number;
        // Volume share of the scope, for the bar width / context.
        share: number;
        // Guests vs prior period for Performance; null for Live.
        deltaPct: number | null;
        reservations: number;
        /** Compare-period metric value for this source (present when `comparable`). */
        compareValue?: number;
        /** Compare-period volume share (0–1). */
        compareShare?: number;
        /** Signed delta vs compare period (pct for rate/avg, pt for share). */
        compareDeltaPct?: number | null;
      }[];
    }
  | {
      kind: 'narrative';
      sev: InsightSeverity;
      title: string;
      why: string;
      action: string;
      impact?: { value: string; label: string } | null;
      /** Opportunity drill: "no clear opportunity in this period". */
      empty?: boolean;
    }
  | {
      kind: 'regimeGrid';
      title: string;
      // Human label for the active capacity basis ("Seats" / "Online cap" /
      // "Physical capacity"), shown alongside the grid.
      basisLabel: string;
      subtitle?: string;
      // Slot resolution and the shared time axis the per-day series map onto.
      slotMinutes: number;
      // Minutes-of-day of the first slot in each day's `series` (shared axis).
      axisStart: number;
      // Minutes-of-day of the slot after the last one in `series`.
      axisEnd: number;
      days: RegimeDay[];
      roomsOpen: RegimeRoomsOpen;
    }
  | {
      kind: 'blockedImpact';
      title: string;
      // Estimated seats × hours removed from availability over the period.
      seatHours: number;
      availableSeatHours: number;
      blockedPct: number;
      // Blocked slot-instances (date-level closures count as a full service).
      totalEvents: number;
      byType: { label: string; count: number }[];
      byDow: { dow: number; label: string; events: number }[];
      note?: string;
      /** Per-bucket blocked capacity % aligned with the dashboard aggregation. */
      series?: number[];
      seriesLabel?: string;
      /** Compare-period blocked-capacity series, aligned 1:1 with `series`. */
      compareSeries?: number[];
    }
  | {
      kind: 'brainSeating';
      title: string;
      autoSeated: number;
      booked: number;
      autoPct: number;
      autoCovers: number;
      covers: number;
      seatable: number;
      coveragePct: number;
      /** Adoption by shift (n≥3). Seat-in origin is BQ-unavailable and usually empty. */
      byShift?: {
        shiftId: string;
        label: string;
        autoSeated: number;
        booked: number;
        /** Reservations with a recorded seating origin: the autoPct base. */
        seatable?: number;
        autoPct: number;
        autoCovers: number;
        covers: number;
      }[];
      byOrigin: {
        origin: string;
        label: string;
        autoSeated: number;
        booked: number;
        autoPct: number;
        autoCovers: number;
        covers: number;
      }[];
      series?: number[];
      seriesLabel?: string;
    }
  | {
      kind: 'settingsRecommendations';
      title: string;
      issues: SettingsIssueGroup[];
    }
  | {
      kind: 'plan';
      title: string;
      items: NorthStarPlanItem[];
      generatedAt?: string;
    };

// One day-of-week column of the occupancy regime grid. `series` is the average
// concurrent-occupancy percentage per 15-min slot across the shared axis.
export type RegimeShiftBand = {
  label: string;
  startMin: number;
  endMin: number;
};

export type RegimeRoomsOpenShift = {
  label: string;
  startMin: number;
  endMin: number;
  totalSeats: number;
  outlets: { label: string; seats: number }[];
};

export type RegimeRoomsOpen = {
  shifts: RegimeRoomsOpenShift[];
  /** Additional distinct shift schedules hidden after the cap. */
  moreCount?: number;
  /** Distinct outlets represented in the summary. */
  outletCount?: number;
};

export type RegimeDay = {
  // ISO weekday 1..7 (Mon..Sun).
  dow: number;
  label: string;
  // Representative available capacity (seats) for the day under the basis.
  capacity: number;
  // Distinct operating dates this weekday appeared in the period.
  operatingDays: number;
  // Peak occupancy % across the day and the slot it occurs in.
  peakPct: number;
  peakLabel: string;
  // Busy-hours (open window) average occupancy %.
  avgPct: number;
  openLabel: string;
  // Occupancy % per slot, aligned 1:1 with the panel's shared axis. Slots
  // outside this day's operating window are null.
  series: (number | null)[];
  // Configured shift windows for this weekday column.
  shiftBands: RegimeShiftBand[];
  /** Average occupied seats per slot, aligned with `series`. */
  seatsSeries?: number[];
  /** Seats on offer per slot (0 when closed), aligned with `series`. */
  capacitySeries?: number[];
};

/** Occupancy hero: occupied ÷ available seat hours (max 100). */
export type SeatHourOccupancy = {
  occupiedSeatHours: number;
  availableSeatHours: number;
  pct: number;
  /** Restaurant-days with seat hours on offer. */
  openDays: number;
  /** Seat hours guests sat on hidden / locked days, left out of both sides. */
  blockedDayOccupiedSeatHours?: number;
  comparePct?: number;
  compareOccupiedSeatHours?: number;
  compareAvailableSeatHours?: number;
};

/** A shift's busiest slot on a typical day it ran. */
export type OccupancyRhythmShift = {
  label: string;
  startMin: number | null;
  endMin: number | null;
  peakMinute: number;
  peakLabel: string;
  capacity: number;
  occupiedSeats: number;
  emptySeats: number;
  /** Days the shift had guests. */
  n: number;
};

/** Typical day of one venue: seats taken vs seats on offer per slot. */
export type OccupancyRhythm = {
  slotMinutes: number;
  axisStart: number;
  axisEnd: number;
  seats: number[];
  capacity: number[];
  /** Days with guests behind the typical day. */
  n: number;
  shifts: OccupancyRhythmShift[];
};

export type RoomSplitRow = {
  id: string;
  name: string;
  outletId: string;
  outletName: string | null;
  seats: number;
  pct: number;
  occupiedSeatHours: number;
  availableSeatHours: number;
  reservations: number;
  guests: number;
};

export type RoomSplit = {
  rows: RoomSplitRow[];
  moreCount?: number;
  /** Reservations behind the rows. */
  n: number;
  /** Share of occupied seat hours on reservations with a room. */
  coveragePct: number;
};

export type TurnsShiftRow = {
  label: string;
  turns: number;
  guests: number;
  seatDays: number;
  /** Service days the shift had guests. */
  n: number;
  /** "Other times": no known shift, or small shifts folded together. */
  other: boolean;
};

/** Turns per operating day next to the calendar-day figure and seat-hour use. */
export type TurnsCapacity = {
  perOperatingDay: number;
  perCalendarDay: number;
  /** Venue-days with served guests. */
  operatingDays: number;
  /** Distinct service days with guests. */
  serviceDates: number;
  calendarDays: number;
  seats: number;
  guests: number;
  byShift: TurnsShiftRow[];
  seatHourPct: number;
  occupiedSeatHours: number;
  availableSeatHours: number;
  reconciliation: string | null;
  comparePerOperatingDay?: number;
  compareSeatHourPct?: number | null;
};

export type DrillDownSecondary = {
  key: string;
  groupValue: number;
  /** null: the compare window has no basis (a venue or data source newer than it). */
  trendPct: number | null;
  trendUnit?: string;
  series: number[];
  byRegion: { region: string; value: number; count: number }[];
  outlets: { id: string; name: string; value: number }[];
};

/** One funnel stage: reservations, their guests and share of requests (0–100). */
export type ReservationFunnelStage = {
  reservations: number;
  guests: number;
  sharePct: number;
};

export type ReservationFunnelLeakKey = 'cancelled' | 'noshow' | 'rejected' | 'pending';

/** One weekday × service cell of the rejection grid; `requests` is its n. */
export type RejectionGridCell = {
  requests: number;
  rejected: number;
  /** Rejected / requests in %; null without requests. */
  ratePct: number | null;
  /** Fewer requests than `minRequests`: drawn muted. */
  thin: boolean;
};

/**
 * Reservations drill funnel (backend reservation_funnel.py): every request
 * lands in served, still ahead or one leak, so they add up to `requested`.
 * Confirmed = served + still ahead = the hero's Reservations.
 */
export type ReservationFunnel = {
  requested: { reservations: number; guests: number };
  confirmed: ReservationFunnelStage;
  served: ReservationFunnelStage;
  upcoming: ReservationFunnelStage;
  leaks: (ReservationFunnelStage & { key: ReservationFunnelLeakKey })[];
  /** Rejected over every request (the scorecard's rejected share); null without requests. */
  rejectionRate: number | null;
  /** Every request in scope: the rejection rate's base. */
  rateBase: number;
  statusFiltered: boolean;
  prior?: {
    requested: { reservations: number; guests: number };
    confirmed: ReservationFunnelStage;
    rejectionRate: number | null;
    rateBase: number;
  };
  rejectionRateDeltaPt?: number;
  grid: {
    /** Columns are the scope's shifts, or dayparts when no shift config names them. */
    kind: 'shift' | 'daypart';
    /** `label` null = requests no shift config names. */
    columns: { key: string; label: string | null }[];
    /** ISO weekday 1 (Mon) … 7 (Sun), cells aligned with `columns`. */
    rows: ({ dow: number; cells: (RejectionGridCell & { key: string })[] } & RejectionGridCell)[];
    columnTotals: (RejectionGridCell & { key: string })[];
    minRequests: number;
  };
};

export type DrillDownData = {
  key: string;
  groupValue: number;
  /** null: the compare window has no basis. */
  trendPct: number | null;
  series: number[];
  byRegion: { region: string; value: number; count: number }[];
  outlets: { id: string; name: string; value: number }[];
  insight: string;
  perspectives?: DrillDownPerspective[];
  // Metric-specific panels (drivers, rankings, narratives, summary cards).
  panels?: DrillPanel[];
  /** Paired metric for combined drill-down screens (covers+reservations, no-show+cancel). */
  secondary?: DrillDownSecondary;
  // Which time frame the drill-down covers and how to caption the hero delta /
  // series. 'today' = Live Service (now/today only); 'period' = Performance
  // (historical, scoped to the reporting period). These keep the two
  // experiences from conflating now vs history.
  timeframe?: 'today' | 'period';
  trendLabel?: string;
  trendUnit?: string;
  seriesLabel?: string;
  secondarySeriesLabel?: string;
  /** Fresh by-source mix for this drill (preferred over scorecard sourceBreakdown). */
  sourceBreakdown?: SourceBreakdown;
  /** Display unit for money drills (POS currency symbol/code, or "€" for sell-items). */
  unit?: string;
  /** True when a non-`off` compare period yielded reference data. Gates every compare affordance. */
  comparable?: boolean;
  /** Resolved compare period (B) — present only when `comparable`. */
  comparePeriod?: {
    kind: PerfCompareKind;
    label: string;
    start: string;
    end: string;
    bucketsAligned: boolean;
  };
  /** Compare-period total for the hero metric (the "B" side of `trendPct`). */
  compareGroupValue?: number;
  /** Per-bucket compare-period series, aligned 1:1 with `series`. Empty when bucket counts mismatch. */
  compareSeries?: number[];
  /** The compare window (ISO days) behind each `compareSeries` point, for its label. */
  compareBuckets?: Array<{ start: string; end: string }>;
  /** Weekday × shift grid (covers, reservations, rates, turns, Brain, seat time). */
  grid?: DrillGrid;
  /** Guests by weekday × hour; the grid's weekday rows narrow it client-side. */
  hourProfile?: DrillHourProfile;
  /** Guests drill only: per operating day and walk-in vs reservation guests. */
  guestsHero?: DrillGuestsHero;
  /** Reservations and guests behind the hero (trend tile n). */
  basis?: DrillBasis;
  /** Reservations and guests behind each outlet row, by outlet id. */
  outletBasis?: Record<string, DrillBasis>;
  /** One line: what the hero counts (drill.definition.<metric>). */
  definition?: string;
  /** Reservations drill only: requests → confirmed → served with the leaks. */
  funnel?: ReservationFunnel;
  /** Occupied ÷ available seat hours (Occupancy hero; Turns' seat-hour use). */
  seatHourOccupancy?: SeatHourOccupancy;
  /** Occupancy drill: per-room occupancy, null with fewer than two rooms. */
  roomSplit?: RoomSplit | null;
  /** Occupancy drill, single venue: typical day against capacity. */
  occupancyRhythm?: OccupancyRhythm | null;
  /** Turns drill: operating-day and calendar-day turns, per shift. */
  turnsCapacity?: TurnsCapacity;
  /** No-show / cancellation drills: guests lost and what they are worth. */
  impact?: DrillImpact;
  /** Cancellation drills: when guests cancel, by the reservation's last change. */
  cancelTiming?: DrillCancelTiming;
  /** No-show / cancellation drills: the rate with and without each guard. */
  guardEffects?: DrillGuardEffects;
  /** No-show drill: guests with repeated no-shows over twelve months. */
  repeatNoShows?: DrillRepeatNoShows;
  /** POS drills: money by hour, with per-shift revenue. */
  posHours?: DrillPosHours;
  /** POS drills: revenue of baskets linked to a reservation vs not. */
  posAttribution?: DrillPosAttribution;
  /** POS drills: open hours with no basket and no arriving guest. */
  posDeadHours?: DrillPosDeadHours;
  /** Sell items: attach rate and pre-order revenue per guest. */
  sellAttach?: DrillSellAttach;
  /** Sell items: top items per shift. */
  sellShiftMix?: DrillSellShiftMix;
  /** Sell items: no-show with vs without a pre-order (only when significant). */
  sellNoShow?: DrillSellNoShow;
};

/** The line under a no-show / cancellation hero (backend noshow_substance.py). */
export type DrillImpact = {
  items: {
    metricKey: 'noshow' | 'cancel';
    reservations: number;
    guests: number;
    lostRevenue: number | null;
    text: string;
  }[];
  basis: {
    kind: 'pos' | 'sell' | 'none';
    perGuest: number | null;
    unit: string | null;
    text: string;
  };
};

export type DrillCancelTimingBucketKey =
  | 'sameDay'
  | 'dayBefore'
  | 'week'
  | 'earlier'
  | 'after'
  | 'unknown';

export type DrillCancelTiming = {
  /** Guest cancellations in the period. */
  n: number;
  guests: number;
  buckets: {
    key: DrillCancelTimingBucketKey;
    n: number;
    guests: number;
    sharePct: number;
    /** Below the driver minimum: drawn pale, never a finding. */
    muted: boolean;
  }[];
  resellable: number;
  resellablePct: number;
  resaleHours: number;
  resaleSource: 'default' | 'config';
  medianHoursAhead: number | null;
  minSample: number;
};

export type DrillGuardArm = { num: number; n: number; rate: number };

export type DrillGuardEffects = {
  metricKey: 'noshow' | 'cancel';
  minSample: number;
  guards: {
    key: 'card' | 'reminder';
    with: DrillGuardArm;
    without: DrillGuardArm;
    diffPts: number;
    z: number;
    /** The same comparison inside each channel (Cochran–Mantel–Haenszel); null without a channel holding both groups. */
    withinChannels: { z: number; diffPts: number; strata: number } | null;
    /** Passes the significance rule and holds within channels. */
    significant: boolean;
    /** Passes over the scope but not within channels: it follows the channel mix. */
    confounded: boolean;
    direction: 'lower' | 'higher' | null;
  }[];
};

export type DrillRepeatNoShows = {
  periodNoshows: number;
  repeatGuests: number;
  repeatPeriodNoshows: number;
  sharePct: number;
  minNoshows: number;
  lookbackDays: number;
  truncated: boolean;
  guests: {
    /** aleno's guest id, the one staff can search for. */
    guestId: string;
    venueId: string | null;
    venue: string | null;
    noShows12m: number;
    noShowsInPeriod: number;
    reservations12m: number;
    lastNoShow: string | null;
  }[];
};

export type OpsCommandLivePayload = {
  outlets: Outlet[];
  kpis: GroupKpis;
  regions: RegionSummary[];
  scopeGroups: ScopeGroupSummary[];
  totalOutletCount: number;
  referenceDate: string;
  connected: ConnectedSourceId[];
  groupName?: string;
  shiftCapacity?: ShiftCapacityInfo;
  comparisons?: KpiComparisons;
  hourlyCovers?: number[];
  hourlyConcurrent?: number[];
  // Forecast and recovery are derived from the same scoped outlet set and are
  // bundled into the live payload so the initial render does not need separate
  // getForecast/getRecovery round-trips.
  forecast?: ForecastData;
  recovery?: RecoveryData;
  timelineBenchmarks?: ServiceTimelineBenchmarks;
  /** GraphQL-backed timeslots + reservation bars for timeline Curve/Gantt. */
  serviceTimeline?: ServiceTimelineOps;
  /** VIP / tags / food / seating / request aggregates for staff briefing. */
  kitchenBriefing?: KitchenBriefing;
  // Today's reservation-source mix (widget / Reserve with Google / staff /
  // walk-ins) across the scoped outlets, for the source comparison module and
  // KPI source distribution. Respects the active source filter when set.
  sourceBreakdown?: SourceBreakdown;
  // The reservation sources the rest of the payload is filtered to (empty =
  // no filter).
  activeSource?: ReservationSourceId[];
  // Active reservation-status groups when filtered (empty = no filter).
  activeStatus?: ReservationStatusGroup[];
  // Configured shift names the payload is filtered to (empty = all).
  activeShifts?: string[];
  availableShifts?: ShiftCatalogItem[];
  // Capacity denominator used for occupancy / turns (defaults to seats).
  activeCapacityBasis?: CapacityBasis;
  /** True when GraphQL reservation pagination hit the server page cap. */
  truncated?: boolean;
  truncatedRestaurantIds?: string[];
};

/** Scope picker metadata - always fetched regardless of live/performance mode. */
export type OpsCommandScopePayload = {
  regions: RegionSummary[];
  scopeGroups: ScopeGroupSummary[];
  totalOutletCount: number;
  /** Membership of the operational filter — not open-now, not painted rows. */
  scopedOutletCount: number;
  referenceDate: string;
  connected: ConnectedSourceId[];
  /** The fleet's real RestaurantGroup name (e.g. "L'Osteria"), when shared. */
  groupName?: string;
  availableShifts?: ShiftCatalogItem[];
};

export type RecoveryOpportunityType =
  | 'recoverable'
  | 'noshow'
  | 'waitlist'
  | 'surge'
  | 'largeparty';

// A concrete recovery action drawn from real aleno features (waitlist, msgIn,
// reconfirmation SMS, Walk-In, widget capacity, aleno Pay).
export type RecoveryLever = {
  id: 'refill' | 'waitlist' | 'event' | 'reconfirm';
  icon: string;
  label: string;
  how: string;
};

export type RecoveryOpportunity = {
  id: string;
  outletId: string;
  name: string;
  city: string;
  region: string;
  type: RecoveryOpportunityType;
  cov: number;
  eur: number;
  // Number of freed tables / parties behind this opportunity (where known).
  count?: number;
  // Short urgency/context cue for the row (e.g. "3 waiting · slot still open").
  why: string;
  // The recommended tool to act on it.
  lever: RecoveryLever;
};

export type RecoveryRegionRow = {
  region: string;
  sites: number;
  // Cancelled / no-show table-gaps today and how they resolved.
  gapCov: number;
  recoveredCov: number;
  recoveredEur: number;
  recoveredCount: number;
  recoverableCov: number;
  recoverableEur: number;
  lostCov: number;
  lostEur: number;
  // Capture rate so far today = recovered / (recovered + lost) resolved gaps.
  capture: number | null;
};

export type RecoveryPattern = {
  icon: string;
  tone: 'alert' | 'win' | 'info';
  text: string;
};

export type RecoveryData = {
  hasPos: boolean;
  // Still-recoverable table-gaps the floor can act on right now (snapshot).
  opportunities: RecoveryOpportunity[];
  // "On the table right now" = covers still recoverable (slot ahead, table free).
  onTableCov: number;
  onTableEur: number;
  onTableCount: number;
  // Recovered today: a freed table rebooked within ±120 min of the gap start.
  recoveredCov: number;
  recoveredEur: number;
  recoveredCount: number;
  // Still recoverable: freed tables whose slot is still ahead of now.
  recoverableCov: number;
  recoverableEur: number;
  recoverableCount: number;
  // Lost today: a freed table whose slot passed with no rebooking.
  lostCov: number;
  lostEur: number;
  lostCount: number;
  // Total table-gaps today (recovered + recoverable + lost).
  gapCov: number;
  gapEur: number;
  gapCount: number;
  // Cancellations / no-shows with no table assignment - cannot be matched.
  gapUnknownCount: number;
  // Capture rate so far today = recovered / (recovered + lost); null when no gap
  // has resolved yet.
  captureRate: number | null;
  // Historical recovery rate for the same day-of-week × shift regime, as a
  // baseline to read today's capture against.
  baselineCaptureRate: number | null;
  baselineSample: number;
  regimeLabel: string | null;
  // Walk-ins seated today - demand captured off the books. Kept prominent as a
  // secondary tally, not part of the gap ledger.
  walkInCov: number;
  walkInEur: number;
  walkInCount: number;
  byRegion: RecoveryRegionRow[];
  patterns: RecoveryPattern[];
};

export type RecoveryBaseline = {
  captureRate: number | null;
  sample: number;
  regimeLabel: string | null;
};

// A single category of "blocked" time in the reporting period (a hidden shift
// date, an online/staff lock, a locked timeslot, or a holiday closure).
export type BlockedTypeCount = {
  label: string;
  count: number;
};

export type BlockedSummary = {
  total: number;
  byType: BlockedTypeCount[];
};

// Per-shift throughput over the reporting period: realized covers against the
// shift's seat capacity across the days it actually operated.
export type ShiftUtilizationRow = {
  shiftId: string;
  name: string;
  covers: number;
  operatingDays: number;
  seats: number;
  // Guests per seat-day across operating days in the period.
  turnsPerSeatDay: number;
  // Per-bucket turns per seat-day across the frame, aligned 1:1 with the
  // payload's `series` buckets, for the in-row trend.
  series?: number[];
};

// A statistically-gated, segment-level insight ("nugget") computed server-side
// from the period's reservation rows - cancel/no-show by day x daypart, party
// size, lead time, guest type and shift. Merged into the Performance hero's
// risk/win highlights board alongside the momentum signals. Measured-only:
// exposure is expressed in covers and percentages, never estimated euros. The
// shape mirrors the hero's internal HeroSignal so the client renders it
// directly; `icon` is one of the hero's known icon names.
export type HeroNugget = {
  tone: 'risk' | 'win';
  icon: string;
  label: string;
  value: string;
  detail: string;
  action: string;
  priority: number;
};

export type OpsCommandPerformancePayload = {
  outlets: Outlet[];
  // Echo of the resolved global settings the payload was built for.
  aggregation: PerfAggregation;
  frameCount: number;
  frameUnit: PerfFrameUnit;
  /** Present when the payload was built for an explicit date range (no prior-period comparison). */
  dateRange?: { start: string; end: string };
  /** Whether comparison deltas vs the reference period are available. */
  comparable?: boolean;
  /**
   * Effective compare kind the payload was built for. Always present, even when
   * off or when there is no prior data (unlike `comparePeriod`).
   */
  compareKind?: PerfCompareKind;
  /** Resolved reference period (B) when comparison is active. */
  comparePeriod?: {
    kind: PerfCompareKind;
    label: string;
    start: string;
    end: string;
    bucketsAligned: boolean;
  };
  /** Per-bucket trend for the reference period when bucket counts match period A. */
  compareSeries?: BucketPoint[];
  /** Resolved ISO service-date bounds for the reporting window. */
  periodStart?: string;
  periodEnd?: string;
  // Human-readable frame ("Last 4 weeks") and the resolved window date span.
  frameLabel: string;
  periodLabel: string;
  // Singular noun for the aggregation bucket ("week"), for chart captions.
  aggregationLabel: string;
  // Last day covered by the reporting window (frames end at the last completed
  // bucket, not today).
  periodEndLabel?: string;
  metrics: PerformanceMetric[];
  // Per-bucket trend across the frame (drives the over-time chart + sparklines).
  series: BucketPoint[];
  outletRows: OutletPerformanceRow[];
  // Operational closures in the period and per-shift throughput.
  // Surfaced under the "Capacity & blocks" section in Performance mode.
  blocked?: BlockedSummary;
  shiftUtilization?: ShiftUtilizationRow[];
  // Segment-level "nugget" insights for the hero highlights board, ranked by
  // significance. Empty/omitted when there isn't enough sample to clear the
  // gating thresholds.
  heroNuggets?: HeroNugget[];
  // Region rollups: covers with share of the period total and the delta vs
  // the immediately preceding period (vsPriorPct - not calendar YoY). `series`
  // is the region's covers per aggregation bucket (1:1 with `series`).
  regions: {
    region: string;
    value: number;
    vsPriorPct: number;
    share: number;
    series?: number[];
  }[];
  // aleno Pay sell-items over the period: most popular pre-ordered items and
  // the revenue they drove, plus revenue by region vs the prior period.
  // `series` is the region's sell-items revenue per aggregation bucket.
  sellTopItems?: SellItem[];
  sellByRegion?: {
    region: string;
    value: number;
    vsPriorPct: number;
    series?: number[];
  }[];
  // Reservation-source mix over the period, with each source's covers vs the
  // prior period (deltaPct) so the source comparison reads as momentum, not
  // just a static split. Always reflects the full unfiltered mix.
  sourceBreakdown?: SourceBreakdown;
  // The reservation sources the rest of the payload is filtered to (empty =
  // no filter).
  activeSource?: ReservationSourceId[];
  // The reservation-status groups the payload is filtered to (empty = all
  // statuses). When non-empty, rate metrics (no-show %, cancel %) are relative
  // to the filtered set.
  activeStatus?: ReservationStatusGroup[];
  // Configured shift names the payload is filtered to (empty = all).
  activeShifts?: string[];
  availableShifts?: ShiftCatalogItem[];
};

// ---------------------------------------------------------------------------
// Insights agent (opsCommand/ask) - conversational analytics.
// Mirrors the server contract in
// backend/imports/app/aleno3/server/opsCommand/agent/schemas.ts. The chart
// `data` is hydrated server-side from a real tool result, so the model never
// authors chart numbers.
// ---------------------------------------------------------------------------

export type ChartType =
  | 'line'
  | 'bar'
  | 'area'
  | 'stackedBar'
  | 'pie'
  | 'heatmap'
  | 'compareBar'
  | 'compareLine'
  | 'compareArea'
  | 'horizontalBar'
  | 'scatter'
  | 'combo';

/** `pct` is percent points (11.4 = 11.4%), `fraction` a share of one (0.114 = 11.4%). */
export type SeriesUnit = 'covers' | 'count' | 'eur' | 'pct' | 'fraction' | 'minutes';

export type ChartSeries = {
  key: string;
  label: string;
  unit?: SeriesUnit;
};

export type QueryRecipe = {
  tool: string;
  args: Record<string, unknown>;
};

export type ChartSpec = {
  type: ChartType;
  title: string;
  x: { key: string; label: string };
  /** Second axis for heatmap (e.g. weekday). */
  y?: { key: string; label: string };
  series: ChartSeries[];
  dataRef: string;
  /** Second tool result joined for compareBar / compareLine / compareArea. */
  compareRef?: string;
  // Hydrated server-side; always present in responses that include a chart.
  data?: Array<Record<string, string | number>>;
  recipe?: QueryRecipe;
  compareRecipe?: QueryRecipe;
};

export type NotebookTemplate =
  | 'kpi_row'
  | 'trend'
  | 'mix'
  | 'ranking'
  | 'compare'
  | 'heatmap'
  | 'table_story'
  | 'multi_viz'
  | 'analysis';

export type NotebookKpi = {
  label: string;
  value: string;
  hint?: string;
  recipe?: QueryRecipe;
  field?: string;
};

export type NotebookWidgetType =
  | 'perf.scorecard'
  | 'perf.northStar'
  | 'perf.highlightsCard'
  | 'perf.situation'
  | 'perf.outletTable'
  | 'live.curve'
  | 'live.gantt'
  | 'live.ribKpis'
  | 'live.segbar'
  | 'live.occupancyRing'
  | 'live.briefGroups'
  | 'live.briefArrivals'
  | 'live.briefAttention'
  | 'live.recovery'
  | 'live.sell'
  | 'live.weather'
  | 'horizon.week'
  | 'horizon.hero'
  | 'horizon.signals';

export type NotebookWidgetSpec = {
  type: NotebookWidgetType;
  title: string;
  params?: Record<string, string>;
};

export type NotebookChapterSpec = {
  template: NotebookTemplate;
  windowLabel: string;
  start: string;
  end: string;
  title?: string;
  story?: string;
  kpis?: NotebookKpi[];
  charts?: ChartSpec[];
  tables?: TableSpec[];
  widgets?: NotebookWidgetSpec[];
};

export type TableColumn = {
  key: string;
  label: string;
  unit?: SeriesUnit;
};

export type TableSpec = {
  title: string;
  columns: TableColumn[];
  dataRef: string;
  // Hydrated server-side; present in responses that include a table.
  data?: Array<Record<string, string | number>>;
  // Total rows available in the source result (data may be capped below this).
  rowCount?: number;
  truncated?: boolean;
  recipe?: QueryRecipe;
};

export type AskScope = {
  restaurantCount: number;
  timezone: string;
  regionFilter: string | null;
  source: string | null;
};

export type NorthStarComponent = {
  metricKey: string;
  weight: number;
  direction: 'up' | 'down';
};

export type NorthStarComposition = 'single' | 'weighted' | 'ratio';

export type NorthStarSpec = {
  composition: NorthStarComposition;
  components: NorthStarComponent[];
  unit: 'score' | 'count' | 'pct' | 'eur' | 'x' | 'min';
  label?: string;
};

export type NorthStarBaseline = {
  value: number;
  window: {
    start: string;
    end: string;
    frameLabel?: string;
  };
  asOf: string;
};

export type NorthStarPlanItem = {
  priority: number;
  category: 'metric' | 'settings' | 'operations';
  title: string;
  why: string;
  action: string;
  metricKey?: string;
};

export type NorthStarPlan = {
  items: NorthStarPlanItem[];
  generatedAt: string;
};

export type NorthStarPayload = {
  label: string;
  description: string;
  spec: NorthStarSpec;
  baseline: NorthStarBaseline;
  plan: NorthStarPlan;
  currentValue: number;
  priorValue: number;
  trendPct: number;
  history: { asOf: string; value: number }[];
  componentBreakdown: Array<{
    metricKey: string;
    label: string;
    value: number;
    prior: number;
    weight: number;
    unit?: string;
    /** Sample behind a rate or average over the window (the drill sets it). */
    n?: number;
    nBasis?: NorthStarSampleBasis;
  }>;
  series: number[];
};

/** What a North Star component's n counts. */
export type NorthStarSampleBasis = 'reservations' | 'seated' | 'receipts';

/** The one window every number on the North Star drill reads. */
export type NorthStarWindow = {
  start: string;
  end: string;
  label: string;
  compareKind: PerfCompareKind;
  compare: { kind: PerfCompareKind; start: string; end: string; label: string } | null;
};

/** getNorthStarDrillDown: the drill plus its window, hero and rebuilt plan. */
export type NorthStarDrillDownData = DrillDownData & {
  window?: NorthStarWindow;
  northStar?: NorthStarPayload;
  plan?: NorthStarPlan;
};

/** Agent-proposed North Star ready for operator confirmation. */
export type NorthStarDraft = {
  label: string;
  description: string;
  spec: NorthStarSpec;
  baseline: NorthStarBaseline;
  plan: NorthStarPlan;
};

/** What "Ask about this" attached to a question (docs/18 §3). */
export type AgentFocusKind =
  | 'insight'
  | 'metric'
  | 'outlet'
  | 'finding'
  | 'horizonDate'
  | 'boardTile'
  | 'northStar'
  | 'card'
  | 'channel';

export type AgentFocus = {
  kind: AgentFocusKind;
  label: string;
  detail?: string;
  metricKey?: string;
  outletId?: string;
  findingId?: string;
  serviceDate?: string;
};

/** Which size renders the answer: the side panel or the expanded home. */
export type AgentSurface = 'panel' | 'expanded';

export type AgentScreen =
  | 'live'
  | 'performance'
  | 'horizon'
  | 'hotel'
  | 'settings'
  | 'growth'
  | 'assistant';

/** Chrome context sent with every question. Answers default to the
 *  Performance window unless the operator names another period. */
export type AskDashboardContext = {
  serviceDateStart?: string;
  serviceDateEnd?: string;
  compareServiceDateStart?: string;
  compareServiceDateEnd?: string;
  comparePeriodLabel?: string;
  periodLabel?: string;
  frameLabel?: string;
  aggregation?: string;
  metricKey?: string;
  frameCount?: number;
  frameUnit?: string;
  dateStart?: string;
  dateEnd?: string;
  /** `north-star` runs the guided flow; everything else is the assistant. */
  mode: 'assistant' | 'north-star';
  surface?: AgentSurface;
  screen?: AgentScreen;
  focus?: AgentFocus;
  /** On Growth: the figures, definitions and windows the screen shows. */
  growth?: AskGrowthContext;
};

/** One headline figure as Growth shows it, with its definition. */
export type AskGrowthMetric = {
  /** Growth's metric id, e.g. `online_reservations`. */
  id: string;
  label: string;
  /** As the screen writes it: "1,857", "24%". */
  value: string;
  raw?: number | null;
  prior?: number | null;
  /** "+38%", "−2.6 pt"; null without a comparison. */
  change?: string | null;
  definition?: string | null;
};

/**
 * What a question asked from Growth is about (WP4.5): the section, venues,
 * Growth's own period and comparison (not Performance's), and the headline
 * figures with their definitions, so the answer explains what the screen shows.
 */
export type AskGrowthContext = {
  section: string;
  venues: string;
  period: { start: string; end: string; label: string } | null;
  compare: {
    /** `default` (last year, same weekdays), `yoy` (last year, same dates), `prior`, `off` or `custom`. */
    choice: string;
    start: string | null;
    end: string | null;
    label: string | null;
  } | null;
  metrics: AskGrowthMetric[];
  /** What to keep in mind, e.g. venues with nothing to compare with. */
  notes?: string[];
};

export type SuggestedAction = {
  id: string;
  label: string;
  question: string;
};

export type CountReceipt = {
  tool?: string;
  windowLocal?: string;
  dateBasis?: string;
  /** reservations = reservation count; served = served guests; metrics = rates and covers. */
  population?: 'reservations' | 'served' | 'metrics';
  statusExplicit?: boolean;
  channelsIncluded?: string[];
  channelsExcluded?: string[];
  channelKeys?: string[];
  excludedChannelKeys?: string[];
  statusesIncluded?: string[];
  statusKeys?: string[];
  excludedStatusKeys?: string[];
  /** Sent by older answers only; ignored. */
  statusDefault?: boolean;
  /** No status filter: every status, every reservation but moved ones, as Performance's "All statuses". */
  statusEvery?: boolean;
  shift?: string;
  excludesCancellations?: boolean;
  excludesNoShows?: boolean;
  excludesWalkInChannel?: boolean;
  excludesWalkInStatus?: boolean;
};

/** One filter part of an answer's receipt: what the tools applied (Phase C). */
export type ReceiptFilterPart = {
  /** What the person had set in the conversation's context. */
  chosen: string[];
  /** The context's values the tools applied. */
  applied: string[];
  /** Values the question set itself (\"including cancellations\"). */
  question?: { values: string[]; exclude?: boolean }[];
  /** Families of figures a chosen filter didn't reach: the tool can't, or the screen doesn't use it. */
  notApplied?: { tool?: string[]; screen?: string[] };
};

export type ReceiptWindow = {
  start: string;
  end: string;
  /** period, comparison, own (Forecast's or Live's window), within (narrower), focus, outside. */
  role: 'period' | 'comparison' | 'own' | 'within' | 'focus' | 'outside';
  kind?: string;
  implicit?: string;
  families?: string[];
};

/**
 * What an answer's tools actually read (backend answer_receipt.py): the
 * venues, windows, statuses, sources and shifts, the definitions of the
 * figures, and what was refused as outside the conversation's context.
 */
export type AnswerContextReceipt = {
  v: 1;
  venues: { ids: string[]; count: number; matchesContext: boolean; names?: string[] };
  windows: ReceiptWindow[];
  comparisonRead: boolean;
  ownComparison?: string[];
  statuses: ReceiptFilterPart;
  sources: ReceiptFilterPart;
  shifts: ReceiptFilterPart;
  definitions: string[];
  families: string[];
  dateBasis?: 'creation';
  outOfContext?: { tool?: string; dates: { start: string; end: string }[]; venues: { id: string; name: string }[] }[];
};

export type AskResponse = {
  answer: string;
  chart: ChartSpec | null;
  tables?: TableSpec[];
  chapter?: NotebookChapterSpec | null;
  scope: AskScope;
  northStarDraft?: NorthStarDraft | null;
  /** True when the North Star agent stated a next step and the UI should offer Proceed. */
  awaitingProceed?: boolean;
  refused?: boolean;
  suggestedActions?: SuggestedAction[];
  receipts?: CountReceipt[];
  /** What the tools read, beside the count receipts (Phase C). */
  contextReceipt?: AnswerContextReceipt;
  /** The context maître d'AI offers to switch to when the question needed more. */
  contextProposal?: import('./lib/conversationContext').ContextProposal;
  /** The conversation's context the answer was bound to, as the server recorded it. */
  conversationContext?: {
    recordedAt?: string | null;
    restaurantId?: string | null;
    screen?: string | null;
    viewContext?: import('./lib/viewContext').RequestViewContext | null;
    recorded?: boolean;
    changed?: boolean;
  };
};

export type AskHistoryTurn = {
  role: 'user' | 'assistant';
  content: string;
  receipts?: CountReceipt[];
};

/** Progress events streamed while the agent runs (POST /api/ask/stream). */
export type AgentProgressEvent =
  | { type: 'status'; message: string }
  | { type: 'thinking'; text: string }
  | {
      type: 'tool_start';
      stepId: string;
      tool: string;
      label: string;
      detail?: string;
    }
  | { type: 'tool_done'; stepId: string; tool: string }
  | { type: 'composing' }
  | { type: 'delta'; text: string }
  | { type: 'mode'; mode: 'north-star' }
  | {
      type: 'navigate';
      kind: string;
      mode?: 'live' | 'performance' | 'settings';
      metricKey?: string;
      outletId?: string;
      reservationId?: string;
      findingId?: string;
      serviceDate?: string;
      northStar?: boolean;
    }
  | { type: 'done'; response: AskResponse }
  | { type: 'error'; message: string };

// --- POS by hour / sell-item substance (drill top-level keys) -------------

export type DrillPosHourRow = {
  hour: number;
  value: number;
  revenue: number;
  baskets: number;
  guests?: number;
  sharePct: number;
};

export type DrillPosShiftRow = {
  /** Shift display name, a daypart id, or `_outside`. */
  key: string;
  label: string | null;
  startMin?: number | null;
  endMin?: number | null;
  revenue: number;
  baskets: number;
  sharePct: number;
  avgBasket: number;
};

export type DrillPosHours = {
  basis: 'payment' | 'arrival';
  valueKind: 'revenue' | 'perGuest' | 'perSeatHour';
  hours: DrillPosHourRow[];
  totalRevenue: number;
  baskets: number;
  guests?: number;
  peakHour: number;
  seats?: number;
  days?: number;
  openHours?: number;
  /** Average spend without linked guests: revenue by hour instead. */
  fallback?: boolean;
  filtersIgnored?: boolean;
  byShift?: {
    basis: 'shift' | 'daypart';
    rows: DrillPosShiftRow[];
    totalRevenue: number;
    baskets: number;
  } | null;
};

export type DrillPosAttributionArm = {
  revenue: number;
  baskets: number;
  revenueSharePct: number;
  basketSharePct: number;
  avgBasket: number;
};

export type DrillPosAttribution = {
  linked: DrillPosAttributionArm;
  unlinked: DrillPosAttributionArm;
  totalRevenue: number;
  baskets: number;
  filtersIgnored?: boolean;
};

export type DrillPosDeadHours = {
  rows: {
    restaurantId: string;
    outlet?: string | null;
    /** ISO weekday, 1 = Monday. */
    weekday: number;
    startHour: number;
    endHour: number;
    hours: number;
    days: number;
    baskets: number;
  }[];
  more: number;
  deadHours: number;
  checkedHours: number;
  outlets: number;
  multiOutlet?: boolean;
};

export type DrillSellAttachFigures = {
  attachRatePct: number;
  preorderReservations: number;
  servedReservations: number;
  revenuePerGuest: number;
  preorderGuests: number;
  preorderRevenue: number;
};

export type DrillSellAttach = DrillSellAttachFigures & {
  compare?: DrillSellAttachFigures;
  attachDeltaPt?: number;
  revenuePerGuestDeltaPct?: number | null;
};

export type DrillSellShiftMix = {
  shifts: {
    key: string;
    label: string | null;
    reservations: number;
    revenue: number;
    items: { name: string; emoji?: string | null; qty: number; revenue: number }[];
  }[];
  more: number;
};

export type DrillSellNoShowArm = {
  ratePct: number;
  noshow: number;
  reservations: number;
};

export type DrillSellNoShow = {
  direction: 'less' | 'more';
  preorder: DrillSellNoShowArm;
  without: DrillSellNoShowArm;
  gapPt: number;
  z: number;
  minSample: number;
};
